/**
 * Destination Experience cinematic splash — playback init for one overlay instance.
 * Not a global singleton: each ExperienceOverlay constructs its own session.
 *
 * CommonJS so Node 12 contract tests can require() this file without Babel.
 */

function createCinematicPlaybackSession() {
  return {
    playInitStarted: false,
    generation: 0,
    cancelled: false,
    srcBound: false,
    src: "",
    poster: "",
    audioSrc: "",
  };
}

function snapshotCinematicMedia(session, media) {
  if (!session) {
    return { src: "", poster: "", audioSrc: "" };
  }
  if (session.srcBound) {
    return {
      src: session.src,
      poster: session.poster,
      audioSrc: session.audioSrc,
    };
  }
  var m = media && typeof media === "object" ? media : {};
  session.src = m.video ? String(m.video) : "";
  session.poster = m.poster ? String(m.poster) : "";
  session.audioSrc = m.audio ? String(m.audio) : "";
  session.srcBound = true;
  return {
    src: session.src,
    poster: session.poster,
    audioSrc: session.audioSrc,
  };
}

function isUsablePromise(value) {
  return !!(value && typeof value.then === "function");
}

function safePlay(video) {
  if (!video || typeof video.play !== "function") {
    return null;
  }
  try {
    return video.play();
  } catch (e) {
    return null;
  }
}

function sessionIsCurrent(session, generation) {
  return !!(
    session &&
    !session.cancelled &&
    session.generation === generation
  );
}

function applyMutedAutoplayFallback(video) {
  if (!video) {
    return;
  }
  video.muted = true;
  if (video.paused) {
    var mutedPlay = safePlay(video);
    if (isUsablePromise(mutedPlay)) {
      mutedPlay.catch(function () {});
    }
  }
}

function errorLabel(err) {
  if (!err) {
    return "";
  }
  if (typeof err === "string") {
    return err;
  }
  return String((err.name || "") + " " + (err.message || err));
}

function isAbortError(err) {
  if (!err) {
    return false;
  }
  if (err.name === "AbortError") {
    return true;
  }
  return /AbortError/i.test(errorLabel(err));
}

function isAutoplayPolicyError(err) {
  if (!err) {
    return false;
  }
  var name = err.name || "";
  if (name === "NotAllowedError" || name === "NotSupportedError") {
    return true;
  }
  var label = errorLabel(err);
  return /NotAllowedError/i.test(label) || /NotSupportedError/i.test(label);
}

function beginCinematicPlayInit(session, video, hooks) {
  hooks = hooks || {};
  if (!session || !video) {
    return;
  }
  if (session.playInitStarted || session.cancelled) {
    return;
  }
  session.playInitStarted = true;
  var generation = session.generation;
  video.playsInline = true;

  function finishUnmuted() {
    if (!sessionIsCurrent(session, generation)) {
      return;
    }
    if (typeof hooks.onUnmuted === "function") {
      hooks.onUnmuted();
    }
  }

  function finishMuted() {
    if (!sessionIsCurrent(session, generation)) {
      return;
    }
    applyMutedAutoplayFallback(video);
    if (typeof hooks.onMutedFallback === "function") {
      hooks.onMutedFallback();
    }
  }

  // Element may already be playing (rare) — do not restart.
  if (!video.paused) {
    if (video.muted) {
      if (typeof hooks.onMutedFallback === "function") {
        hooks.onMutedFallback();
      }
    } else {
      finishUnmuted();
    }
    return;
  }

  video.muted = false;
  var playPromise = safePlay(video);
  if (!isUsablePromise(playPromise)) {
    if (video.paused) {
      finishMuted();
    } else {
      finishUnmuted();
    }
    return;
  }

  playPromise
    .then(function () {
      finishUnmuted();
    })
    .catch(function (err) {
      if (!sessionIsCurrent(session, generation)) {
        return;
      }
      // AbortError is a lifecycle/race interrupt — leave playback alone.
      if (isAbortError(err)) {
        if (!video.paused && !video.muted) {
          finishUnmuted();
        }
        return;
      }
      if (isAutoplayPolicyError(err)) {
        finishMuted();
      }
    });
}

function unmuteCinematicPlayback(video) {
  if (!video) {
    return;
  }
  video.muted = false;
  if (video.paused) {
    var resume = safePlay(video);
    if (isUsablePromise(resume)) {
      resume.catch(function () {});
    }
  }
}

function cleanupCinematicPlayback(session, video) {
  if (session) {
    session.cancelled = true;
    session.generation = (session.generation || 0) + 1;
  }
  if (video && typeof video.pause === "function") {
    try {
      video.pause();
    } catch (e) {}
  }
}

exports.__esModule = true;
exports.createCinematicPlaybackSession = createCinematicPlaybackSession;
exports.snapshotCinematicMedia = snapshotCinematicMedia;
exports.beginCinematicPlayInit = beginCinematicPlayInit;
exports.applyMutedAutoplayFallback = applyMutedAutoplayFallback;
exports.unmuteCinematicPlayback = unmuteCinematicPlayback;
exports.cleanupCinematicPlayback = cleanupCinematicPlayback;
exports.isAbortError = isAbortError;
exports.isAutoplayPolicyError = isAutoplayPolicyError;
